import React, { useMemo } from 'react'

import { 
  Layers, Database, Server, Cloud, Users, 
  Globe, Shield, Zap, HardDrive, Cpu, Network, 
  MessageSquare, Bell, Search, BarChart3
} from 'lucide-react'

const SYSTEM_COMPONENTS = [
  { id: 'load-balancer', name: 'Load Balancer', icon: Globe, color: 'bg-blue-500' },
  { id: 'web-server', name: 'Web Server', icon: Server, color: 'bg-green-500' },
  { id: 'app-server', name: 'App Server', icon: Cpu, color: 'bg-purple-500' },
  { id: 'database', name: 'Database', icon: Database, color: 'bg-yellow-500' },
  { id: 'cache', name: 'Cache', icon: Zap, color: 'bg-red-500' },
  { id: 'cdn', name: 'CDN', icon: Cloud, color: 'bg-indigo-500' },
  { id: 'queue', name: 'Message Queue', icon: MessageSquare, color: 'bg-pink-500' },
  { id: 'storage', name: 'File Storage', icon: HardDrive, color: 'bg-gray-500' },
  { id: 'search', name: 'Search Service', icon: Search, color: 'bg-orange-500' },
  { id: 'analytics', name: 'Analytics', icon: BarChart3, color: 'bg-teal-500' },
  { id: 'notification', name: 'Notifications', icon: Bell, color: 'bg-amber-500' },
  { id: 'security', name: 'Security/Auth', icon: Shield, color: 'bg-emerald-500' }
]

type ViewerComponent = {
  id: string
  componentId: string
  name: string
  color: string
  x: number
  y: number
  width: number
  height: number
}

type ViewerConnection = {
  id: string
  type: string
  fromComponentId: string
  toComponentId: string
  color: string
  strokeWidth: number
  label?: string
}

interface ViewerDiagramElements {
  placedComponents?: ViewerComponent[]
  connections?: ViewerConnection[]
  canvasSize?: {
    width?: number
    height?: number
  }
  viewport?: {
    zoom?: number
    panX?: number
    panY?: number
  }
}

interface SystemDesignViewerProps {
  diagramElements?: ViewerDiagramElements
  content?: string
  designNotes?: string
  systemDesignDiscussion?: string
}

const computeBounds = (components: ViewerComponent[] | undefined, canvasSize?: { width?: number; height?: number }) => {
  const baseWidth = canvasSize?.width ? Number(canvasSize.width) : undefined
  const baseHeight = canvasSize?.height ? Number(canvasSize.height) : undefined

  const maxX = components && components.length
    ? Math.max(...components.map(component => component.x + component.width))
    : 0
  const maxY = components && components.length
    ? Math.max(...components.map(component => component.y + component.height))
    : 0

  return {
    width: Math.max(baseWidth ?? 960, maxX + 240),
    height: Math.max(baseHeight ?? 720, maxY + 240)
  }
}

export default function SystemDesignViewer({
  diagramElements,
  content,
  designNotes,
  systemDesignDiscussion
}: SystemDesignViewerProps) {
  const components: ViewerComponent[] = diagramElements?.placedComponents ?? []
  const connections: ViewerConnection[] = diagramElements?.connections ?? []
  const canvasBounds = useMemo(
    () => computeBounds(components, diagramElements?.canvasSize),
    [components, diagramElements?.canvasSize]
  )

  const viewport = diagramElements?.viewport || {}
  const zoom = typeof viewport.zoom === 'number' && viewport.zoom > 0 ? viewport.zoom : 1
  const panX = typeof viewport.panX === 'number' ? viewport.panX : 0
  const panY = typeof viewport.panY === 'number' ? viewport.panY : 0

  const containerHeight = useMemo(() => {
    const scaledHeight = canvasBounds.height * zoom
    return Math.min(Math.max(scaledHeight + 160, 480), 1200)
  }, [canvasBounds.height, zoom])

  const renderConnection = (connection: ViewerConnection) => {
    const fromComponent = components.find(component => component.id === connection.fromComponentId)
    const toComponent = components.find(component => component.id === connection.toComponentId)

    if (!fromComponent || !toComponent) {
      console.log('❌ Connection missing component:', {
        connectionId: connection.id,
        fromId: connection.fromComponentId,
        toId: connection.toComponentId,
        hasFrom: !!fromComponent,
        hasTo: !!toComponent,
        availableIds: components.map(c => c.id)
      })
      return null
    }

    const fromX = fromComponent.x + fromComponent.width / 2
    const fromY = fromComponent.y + fromComponent.height / 2
    const toX = toComponent.x + toComponent.width / 2
    const toY = toComponent.y + toComponent.height / 2

    const midX = fromX + (toX - fromX) * 0.7
    const arrowLength = 8
    const arrowWidth = 4

    const flowArrows: Array<{ x: number; y: number; direction: 'horizontal' | 'vertical'; segment: number }> = []
    const spacing = 60

    const horizontalDist1 = Math.abs(midX - fromX)
    if (horizontalDist1 > spacing) {
      const numArrows1 = Math.floor(horizontalDist1 / spacing)
      for (let i = 1; i <= numArrows1; i++) {
        const x = fromX + (midX - fromX) * (i / (numArrows1 + 1))
        flowArrows.push({ x, y: fromY, direction: 'horizontal', segment: 1 })
      }
    }

    const verticalDist = Math.abs(toY - fromY)
    if (verticalDist > spacing) {
      const numArrows2 = Math.floor(verticalDist / spacing)
      for (let i = 1; i <= numArrows2; i++) {
        const y = fromY + (toY - fromY) * (i / (numArrows2 + 1))
        flowArrows.push({ x: midX, y, direction: 'vertical', segment: 2 })
      }
    }

    const horizontalDist2 = Math.abs(toX - midX)
    if (horizontalDist2 > spacing) {
      const numArrows3 = Math.floor(horizontalDist2 / spacing)
      for (let i = 1; i <= numArrows3; i++) {
        const x = midX + (toX - midX) * (i / (numArrows3 + 1))
        flowArrows.push({ x, y: toY, direction: 'horizontal', segment: 3 })
      }
    }

    return (
      <g key={connection.id}>
        <line x1={fromX} y1={fromY} x2={midX} y2={fromY} stroke={connection.color} strokeWidth={connection.strokeWidth} />
        <line x1={midX} y1={fromY} x2={midX} y2={toY} stroke={connection.color} strokeWidth={connection.strokeWidth} />
        <line x1={midX} y1={toY} x2={toX} y2={toY} stroke={connection.color} strokeWidth={connection.strokeWidth} />

        {flowArrows.map((arrow, index) => {
          const size = 7
          if (arrow.direction === 'horizontal') {
            const direction = arrow.segment === 1 ? (midX > fromX ? 1 : -1) : (toX > midX ? 1 : -1)
            return (
              <polygon
                key={`flow-${index}`}
                points={`${arrow.x + size * direction},${arrow.y} ${arrow.x - size * direction},${arrow.y - size / 2} ${arrow.x - size * direction},${arrow.y + size / 2}`}
                fill={connection.color}
                opacity="0.7"
              />
            )
          }

          const direction = toY > fromY ? 1 : -1
          return (
            <polygon
              key={`flow-${index}`}
              points={`${arrow.x},${arrow.y + size * direction} ${arrow.x - size / 2},${arrow.y - size * direction} ${arrow.x + size / 2},${arrow.y - size * direction}`}
              fill={connection.color}
              opacity="0.7"
            />
          )
        })}

        {connection.type === 'arrow' && (
          <polygon
            points={`${toX},${toY} ${toX - arrowLength},${toY - arrowWidth} ${toX - arrowLength},${toY + arrowWidth}`}
            fill={connection.color}
          />
        )}

        {connection.label && (
          <text
            x={(fromX + toX) / 2 + 8}
            y={(fromY + toY) / 2 - 6}
            fontSize="10"
            fontWeight={600}
            fill={connection.color}
            textAnchor="start"
            className="font-mono"
          >
            {connection.label}
          </text>
        )}
      </g>
    )
  }

  return (
    <div className="space-y-6">
      {components.length > 0 ? (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
            <Layers className="w-4 h-4" />
            <span>System Architecture Diagram</span>
          </h5>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="relative overflow-auto bg-white border-2 border-gray-200 rounded-lg" style={{ height: containerHeight }}>
              <div
                className="relative"
                style={{
                  width: canvasBounds.width,
                  height: canvasBounds.height,
                  transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
                  transformOrigin: '0 0'
                }}
              >
                <svg width={canvasBounds.width} height={canvasBounds.height} style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
                  {connections.map(renderConnection)}
                </svg>

                {components.map(component => {
                  const systemComponent = SYSTEM_COMPONENTS.find(c => c.id === component.componentId)
                  const Icon = systemComponent?.icon || Database
                  const color = systemComponent?.color || component.color

                  return (
                    <div
                      key={component.id}
                      className="absolute shadow-lg rounded-lg border border-gray-200"
                      style={{
                        left: component.x,
                        top: component.y,
                        width: component.width,
                        height: component.height,
                        minWidth: 160,
                        zIndex: 2
                      }}
                    >
                      <div className={`h-2 ${color} rounded-t-lg`}></div>
                      <div className="p-4 bg-white h-full flex flex-col">
                        <div className="flex items-center space-x-3">
                          <div className={`${color} w-10 h-10 rounded-full flex items-center justify-center text-white`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 leading-tight">{component.name}</p>
                            <p className="text-xs text-gray-500">{systemComponent?.name || 'Custom Component'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-gray-300 rounded-lg py-12 text-center text-sm text-gray-500 bg-gray-50">
          No system design components were saved.
        </div>
      )}

      {content && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3">System Overview</h5>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{content}</pre>
          </div>
        </div>
      )}

      {designNotes && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3">Design Notes</h5>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-blue-900 text-sm whitespace-pre-wrap leading-relaxed">{designNotes}</p>
          </div>
        </div>
      )}

      {systemDesignDiscussion && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3">Design Discussion</h5>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <p className="text-purple-900 text-sm whitespace-pre-wrap leading-relaxed">{systemDesignDiscussion}</p>
          </div>
        </div>
      )}
    </div>
  )
}
